import React, { Fragment, useState, useEffect } from 'react';
import styled, { useTheme, css } from 'styled-components';
import PropTypes from 'prop-types';
import faker from '@faker-js/faker';

import { Text, Box, Flex, SvgIcon, List } from 'Components/Base';
import { TIME_UP_LOCKED_TYPES } from 'Components/Classroom/Content/constant';
import { LEARNING_MODE } from 'Context/ClassroomContext';
import useIsClassroomTimeUp from 'Util/Hooks/useIsClassroomTimeUp';

const getCourseSubsectionContainerBackgroundColor = (
  active,
  isSectionGroupActive,
  colors,
  isSubsectionCurrentDefaultMode,
  isSubsectionCurrentLearningMode
) => {
  if (!isSubsectionCurrentLearningMode) {
    return colors.greyscale.grey5;
  }

  if (active) {
    return isSubsectionCurrentDefaultMode ? colors.primary : colors.dangerLight;
  }

  if (isSectionGroupActive) {
    return colors.white;
  }

  return colors.coolGrey.grey2;
};

const CourseSubsectionContainer = styled(({ active, isSectionGroupActive, ...restProps }) => (
  <Flex pt={4} pb={5} px={6} {...restProps} />
))`
  ${({
    theme: { colors },
    active,
    isSectionGroupActive,
    isSubsectionCurrentDefaultMode,
    isSubsectionCurrentLearningMode,
  }) => css`
    background-color: ${getCourseSubsectionContainerBackgroundColor(
      active,
      isSectionGroupActive,
      colors,
      isSubsectionCurrentDefaultMode,
      isSubsectionCurrentLearningMode
    )};
    color: ${active ? colors.white : colors.text};
    border-top: 1px solid ${colors.greyscale.grey4};
    cursor: ${isSubsectionCurrentLearningMode ? 'pointer' : 'default'};
  `};
`;

const CourseSectionContainer = styled(({ active, ...restProps }) => <Box {...restProps} />)`
  ${({ theme: { colors }, active }) => css`
    background-color: ${active ? colors.primary : colors.white};
    color: ${active ? colors.white : colors.text};
  `};
`;

const SvgCompleteIcon = styled(({ ...restProps }) => (
  <SvgIcon icon={SvgIcon.ICON_LIST.CHECK_CIRCLE} {...restProps} />
))`
  ${({ theme: { colors } }) => css`
    color: ${colors.primary};
    flex-shrink: 0;
    height: 24px;
    margin-right: 8px;
  `};
`;

const CourseSection = ({
  section,
  onSectionClick,
  onSubsectionClick,
  name,
  isSectionGroupActive,
  currentLearningMode,
}) => {
  const { colors, space } = useTheme();
  const [subsectionOpen, setSubsectionOpen] = useState(false);
  const { subsections = [], title, displayName } = section;
  const isTimeUp = useIsClassroomTimeUp();

  useEffect(() => {
    const indexOfActiveSubsection = subsections.findIndex((s) => s.active === true);
    if (indexOfActiveSubsection !== -1) {
      setSubsectionOpen(true);
    }
  }, [subsections]);

  const handleToggleSubsectionOpen = (title) => {
    setSubsectionOpen(!subsectionOpen);
    if (typeof onSectionClick === 'function') {
      onSectionClick(title);
    }
  };

  const handleSubsectionClick = ({ section, subsection }) => {
    setSubsectionOpen(true);
    onSubsectionClick({ section, subsection });
  };

  return (
    <Box
      style={{
        borderBottom: `1px solid ${colors.greyscale.grey4}`,
      }}
    >
      <Flex
        data-testid={`${name}-tab`}
        p={4}
        justifyContent={'space-between'}
        onClick={() => handleToggleSubsectionOpen(title)}
      >
        <Text pl={4} textStyle={'body2'} fontWeight={title ? 300 : 400}>
          {title ? title : displayName}
        </Text>
        <SvgIcon
          size={24}
          icon={subsectionOpen ? SvgIcon.ICON_LIST.ARROW_UP : SvgIcon.ICON_LIST.ARROW_DOWN}
        />
      </Flex>

      {subsectionOpen && (
        <List
          items={subsections}
          renderItem={(subsection, index) => {
            const {
              subsectionIndex,
              active,
              complete,
              completedLearningModeList,
              title,
              displayName,
              duration,
              id,
            } = subsection;

            const learningModes =
              subsection.learningModes && subsection.learningModes.length > 0
                ? subsection.learningModes
                : [LEARNING_MODE.DEFAULT];

            const isSubsectionCurrentDefaultMode = currentLearningMode === LEARNING_MODE.DEFAULT;
            const isSubsectionCurrentLearningMode = learningModes
              ? learningModes.includes(currentLearningMode)
              : false;
            // Once learning time runs out, videos look and act like subsections outside the mode
            const isTimeLocked = isTimeUp && TIME_UP_LOCKED_TYPES.includes(subsection.contentType);
            const isSelectable = isSubsectionCurrentLearningMode && !isTimeLocked;

            const shouldCompleted = complete && !active;
            const shouldCompletedCurrentLearningMode =
              completedLearningModeList.includes(currentLearningMode);
            const shouldCompleteIconDark = shouldCompleted && shouldCompletedCurrentLearningMode;

            // TODO: Enabled complete icon light after clear requirement
            // const shouldCompleteIconLight =
            //   shouldCompleted &&
            //   !shouldCompletedCurrentLearningMode &&
            //   isSubsectionCurrentDefaultMode;

            return (
              <CourseSubsectionContainer
                key={id}
                data-testid={`${name}-subsection-${index + 1}-tab`}
                active={active}
                alignItems={'start'}
                isSectionGroupActive={isSectionGroupActive}
                isSubsectionCurrentDefaultMode={isSubsectionCurrentDefaultMode}
                isSubsectionCurrentLearningMode={isSelectable}
                onClick={() => {
                  if (!isSelectable) return;
                  else handleSubsectionClick({ subsection, section });
                }}
              >
                {active ? (
                  <SvgIcon
                    icon={SvgIcon.ICON_LIST.VIDEO_PLAY_CIRCLE}
                    size={24}
                    mr={5}
                    ml={isSectionGroupActive ? 4 : 0}
                    style={{ flexShrink: 0, height: '24px' }}
                  />
                ) : (
                  <Text
                    textStyle={'heading5'}
                    color={isSelectable ? 'inherit' : 'greyscale.grey3'}
                    minWidth={'24px'}
                    textAlign={'center'}
                    style={{ flexShrink: 0 }}
                    pl={4}
                    mr={5}
                  >
                    {isSectionGroupActive ? subsectionIndex + 1 : subsectionIndex}
                  </Text>
                )}

                <Flex style={{ flexGrow: 1 }} alignItems={'start'}>
                  {shouldCompleteIconDark && <SvgCompleteIcon />}
                  {/* TODO: Enabled complete icon light after clear requirement */}
                  {/* {shouldCompleteIconLight && <SvgCompleteIcon style={{ opacity: '32%' }} />} */}
                  <Text color={isSelectable ? 'inherit' : 'greyscale.grey3'}>
                    {title ? title : displayName}
                  </Text>
                </Flex>

                {isSectionGroupActive ? (
                  <Fragment />
                ) : (
                  <Text textStyle={'caption'} color={'inherit'} ml={5}>
                    {duration}
                  </Text>
                )}
              </CourseSubsectionContainer>
            );
          }}
          columns={1}
          childSpacing={'0px'}
          rowSpacing={space[4]}
        />
      )}
    </Box>
  );
};

const CourseSectionGroup = ({
  sectionGroup,
  onSectionGroupClick,
  onSectionClick,
  onSubsectionClick,
  name,
  isSectionGroupActive = false,
  currentLearningMode,
  ...props
}) => {
  const { colors } = useTheme();
  const [sectionGroupOpen, setSectionGroupOpen] = useState(false);
  const { sections = [], title } = sectionGroup;

  useEffect(() => {
    const indexOfActiveSection = sections.findIndex((s) => s.active === true);
    if (indexOfActiveSection !== -1) {
      setSectionGroupOpen(true);
    }
  }, [sections]);

  const handleToggleSectionGroupOpen = (title) => {
    setSectionGroupOpen(!sectionGroupOpen);
    if (typeof onSectionClick === 'function') {
      onSectionGroupClick(title);
    }
  };
  return (
    <Box>
      <Flex
        data-testid={`${name}-tab`}
        p={4}
        justifyContent={'space-between'}
        onClick={() => handleToggleSectionGroupOpen(title)}
        style={{
          borderBottom: `1px solid ${colors.greyscale.grey4}`,
        }}
      >
        <Text fontWeight={'semibold'} textStyle={'body1'}>
          {title}
        </Text>
        <SvgIcon
          size={24}
          icon={sectionGroupOpen ? SvgIcon.ICON_LIST.ARROW_UP : SvgIcon.ICON_LIST.ARROW_DOWN}
        />
      </Flex>

      {sectionGroupOpen && (
        <CourseSectionContainer>
          {sections.map((section, index) => {
            return (
              <CourseSection
                key={section.id}
                name={`toc-flatted-section-${index + 1}`}
                section={section}
                onSubsectionClick={onSubsectionClick}
                onSectionClick={onSectionClick}
                isSectionGroupActive={isSectionGroupActive}
                currentLearningMode={currentLearningMode}
                {...props}
              />
            );
          })}
        </CourseSectionContainer>
      )}
    </Box>
  );
};

const TocFlatted = ({
  isSectionGroupActive = false,
  sectionGroups = [],
  sections = [],
  onSectionGroupClick,
  onSectionClick,
  onSubsectionClick,
  currentLearningMode,
  ...props
}) => {
  if (isSectionGroupActive) {
    return (
      <Fragment>
        {sectionGroups.map((sectionGroup, index) => {
          return (
            <CourseSectionGroup
              isSectionGroupActive={isSectionGroupActive}
              key={sectionGroup.id}
              name={`toc-flatted-section-group-${index + 1}`}
              sectionGroup={sectionGroup}
              onSectionGroupClick={onSectionGroupClick}
              onSubsectionClick={onSubsectionClick}
              onSectionClick={onSectionClick}
              currentLearningMode={currentLearningMode}
              {...props}
            />
          );
        })}
      </Fragment>
    );
  }
  return (
    <Fragment>
      {sections.map((section, index) => {
        return (
          <CourseSection
            isSectionGroupActive={isSectionGroupActive}
            key={section.id}
            name={`toc-flatted-section-${index + 1}`}
            section={section}
            onSubsectionClick={onSubsectionClick}
            onSectionClick={onSectionClick}
            currentLearningMode={currentLearningMode}
            {...props}
          />
        );
      })}
    </Fragment>
  );
};

CourseSection.propTypes = {
  name: PropTypes.string,
};

CourseSection.defaultProps = {
  name: faker.random.uuid(),
};

export default TocFlatted;
